import { useMemo } from 'react';
import { useTaskStore } from '@/stores/taskStore';
import { useHabitStore } from '@/stores/habitStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useUIStore } from '@/stores/uiStore';
import { useCurrentMinute } from '@/hooks/useCurrentMinute';
import type { UpNextItem } from './UpNextList';
import {
  getDefaultNowKey,
  getNowItems,
  getUpNextTasks,
  getUpNextHabits,
  getUpNextCalendarEvents,
  isSameDay,
  type NowItem,
} from './todaySelectors';

export interface TodayAgenda {
  /** Live "now", ticks every minute via useCurrentMinute. */
  now: Date;
  /** The day the user is looking at (today, or a browsed day from the day-strip). */
  viewedDate: Date;
  isViewingToday: boolean;
  /** Every item concurrent with now, in stable start-time display order. */
  nowItems: NowItem[];
  /** The item the Now stack expands when the user hasn't chosen one. */
  defaultNowKey: string | undefined;
  /** Merged, time-sorted up-next list (tasks + habits + calendar events). */
  upNext: UpNextItem[];
}

/**
 * The single source of truth for the Today agenda — the "Right now" stack and
 * the "Up next" list — shared by both the mobile Today view and the desktop
 * Narrator rail so the two surfaces stay in lockstep. Previously the hero
 * cascade mutually suppressed concurrent items (a task hid an overlapping
 * habit and meeting); getNowItems returns them all, and NowStack expands one.
 */
export function useTodayAgenda(upNextLimit = 8): TodayAgenda {
  const tasks = useTaskStore((s) => s.tasks);
  const habits = useHabitStore((s) => s.habits);
  const completions = useHabitStore((s) => s.completions);
  const calendarEvents = useCalendarStore((s) => s.events);
  const viewedDate = useUIStore((s) => s.lastViewedDate);
  const weekStart = useUIStore((s) => s.weekStart);
  const { now } = useCurrentMinute();

  const nowItems = useMemo(
    () => getNowItems(tasks, habits, completions, calendarEvents, now, viewedDate, weekStart),
    [tasks, habits, completions, calendarEvents, now, viewedDate, weekStart],
  );

  const defaultNowKey = useMemo(() => getDefaultNowKey(nowItems), [nowItems]);

  // Every task currently in the Now area — kept out of Up next so an item never
  // appears in both lists (an in_progress task with a future scheduledStart
  // would otherwise pass getUpNextTasks' start > now filter).
  const nowTaskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const i of nowItems) if (i.kind === 'task') ids.add(i.task.id);
    return ids;
  }, [nowItems]);

  const upNextTasks = useMemo(
    () => getUpNextTasks(tasks, now, viewedDate, { limit: 6, excludeIds: nowTaskIds }),
    [tasks, now, viewedDate, nowTaskIds],
  );

  // Habits with start > now on today by construction — can't collide with
  // the Now stack (whose habit windows all contain now).
  const upNextHabits = useMemo(
    () => getUpNextHabits(habits, completions, now, viewedDate, weekStart),
    [habits, completions, now, viewedDate, weekStart],
  );

  const upNextCalEvents = useMemo(
    () => getUpNextCalendarEvents(calendarEvents, now, viewedDate),
    [calendarEvents, now, viewedDate],
  );

  const isViewingToday = isSameDay(viewedDate, now);
  const upNext = useMemo<UpNextItem[]>(() => {
    const items: UpNextItem[] = [
      ...upNextTasks.map((t) => ({ kind: 'task' as const, task: t, start: t.scheduledStart! })),
      ...upNextHabits.map((h) => ({
        kind: 'habit' as const,
        habit: h.habit,
        start: h.start,
        isDone: h.isDone,
      })),
      // getUpNextCalendarEvents keeps any event that hasn't ENDED, which
      // includes ones running right now — those live in the Now area (or were
      // deduped behind their task twin), so Up next lists future starts only.
      ...upNextCalEvents
        .filter((e) => !isViewingToday || e.start.getTime() > now.getTime())
        .map((e) => ({
          kind: 'calendar-event' as const,
          event: e,
          start: e.start,
        })),
    ];
    return items.sort((a, b) => a.start.getTime() - b.start.getTime()).slice(0, upNextLimit);
  }, [upNextTasks, upNextHabits, upNextCalEvents, isViewingToday, now, upNextLimit]);

  return {
    now,
    viewedDate,
    isViewingToday,
    nowItems,
    defaultNowKey,
    upNext,
  };
}
